import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive, ArchiveRestore, ArrowLeft, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge, Empty, PageHeader } from "@/components/common";
import { EquipmentDialog } from "@/components/v3forms";
import { FilesPanel } from "@/components/FilesPanel";
import { api } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { EQUIPMENT_TYPES, SPEC_FIELDS, INTERVENTION_STATUS, CONTRACT_STATUS, fmtDate, fmtMin } from "@/lib/constants";

const label = (e) => e.name || `${e.brand} ${e.model}`.trim() || e.serial;

export function EquipmentList() {
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const { items, reload } = useList("/equipment", { q, archived });
  return (
    <>
      <PageHeader title="Équipements" subtitle="Parc des équipements de vos clients">
        <Button onClick={() => setOpen(true)} className="dt-glow" data-testid="new-equipment-button"><Plus className="mr-1 h-4 w-4" />Ajouter un équipement</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, marque, modèle, n° de série…" className="pl-9" data-testid="equipment-search-input" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-300"><Switch checked={archived} onCheckedChange={setArchived} data-testid="equipment-archived-switch" />Archivés</label>
      </div>
      {!items.length && <Empty testid="equipment-empty">Aucun équipement.</Empty>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="equipment-list">
        {items.map((e) => (
          <Link key={e.id} to={`/equipements/${e.id}`} className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4 transition-colors hover:border-cyan-400/50" data-testid={`equipment-card-${e.id}`}>
            <p className="font-medium text-white">{label(e)}</p>
            <p className="text-xs text-slate-400">{EQUIPMENT_TYPES[e.type]} · {e.client_name}</p>
            <p className="mt-1 text-xs text-slate-500">{[e.brand, e.model, e.serial && `n° ${e.serial}`, e.location].filter(Boolean).join(" · ")}</p>
          </Link>
        ))}
      </div>
      <EquipmentDialog open={open} onOpenChange={setOpen} initial={{}} onSaved={reload} />
    </>
  );
}

const HistoryItem = ({ h }) => (
  <li className="rounded-lg border border-white/10 p-3" data-testid={`equipment-history-${h.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Link to={`/interventions/${h.id}`} className="font-medium text-cyan-200 hover:underline">{fmtDate(h.date)} · {h.title}</Link>
      <Badge value={h.status} map={INTERVENTION_STATUS} />
    </div>
    {h.diagnostic && <p className="mt-1 text-sm text-slate-300"><span className="text-slate-500">Diagnostic : </span>{h.diagnostic}</p>}
    {h.report && <p className="text-sm text-slate-300"><span className="text-slate-500">Travaux : </span>{h.report}</p>}
    {!!h.parts.length && <p className="text-xs text-slate-400">Pièces : {h.parts.map((p) => `${p.quantity} × ${p.label}`).join(", ")}</p>}
    <p className="text-xs text-slate-500">Durée : {fmtMin(h.retained_minutes ?? h.worked_minutes)}{h.signed_at ? " · bon signé" : ""}</p>
  </li>
);

export function EquipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [e, setE] = useState(null);
  const [hist, setHist] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => {
    api.get(`/equipment/${id}`).then((r) => setE(r.data)).catch(() => nav("/equipements"));
    api.get(`/equipment/${id}/history`).then((r) => setHist(r.data));
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!e) return null;
  const toggleArchive = async () => { await api.patch(`/equipment/${id}`, { archived: !e.archived }); toast.success(e.archived ? "Équipement réactivé" : "Équipement archivé"); load(); };
  return (
    <div className="space-y-5" data-testid="equipment-detail">
      <Link to="/equipements" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-300"><ArrowLeft className="h-4 w-4" />Équipements</Link>
      <PageHeader title={label(e)} subtitle={`${EQUIPMENT_TYPES[e.type]} · ${e.client_name}${e.archived ? " · archivé" : ""}`}>
        <Button variant="secondary" onClick={() => setEdit(true)} data-testid="equipment-edit-button"><Pencil className="mr-1 h-4 w-4" />Modifier</Button>
        <Button variant="ghost" onClick={toggleArchive} data-testid="equipment-archive-button">{e.archived ? <ArchiveRestore className="mr-1 h-4 w-4" /> : <Archive className="mr-1 h-4 w-4" />}{e.archived ? "Réactiver" : "Archiver"}</Button>
        <Button onClick={() => nav(`/interventions/nouvelle?client_id=${e.client_id}`)} className="dt-glow" data-testid="equipment-new-intervention-button"><Plus className="mr-1 h-4 w-4" />Intervention</Button>
      </PageHeader>
      <section className="grid gap-2 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4 sm:grid-cols-3" data-testid="equipment-info">
        {[["Marque", e.brand], ["Modèle", e.model], ["N° de série", e.serial], ["Emplacement", e.location], ["Achat", e.purchase_date && fmtDate(e.purchase_date)],
          ["Fin de garantie", e.warranty_end && fmtDate(e.warranty_end)], ...Object.entries(SPEC_FIELDS).map(([k, l]) => [l, (e.specs || {})[k]])]
          .filter(([, v]) => v).map(([k, v]) => <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="text-sm text-white">{v}</p></div>)}
        {e.notes && <p className="text-sm text-slate-300 sm:col-span-3">{e.notes}</p>}
      </section>
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="equipment-contracts">
        <h2 className="mb-2 text-base font-semibold text-white">Contrats</h2>
        {hist?.contracts.length ? hist.contracts.map((c) => (
          <Link key={c.id} to={`/contrats/${c.id}`} className="mr-3 inline-flex items-center gap-2 text-sm text-cyan-200 hover:underline">{c.number} · {c.name}<Badge value={c.status} map={CONTRACT_STATUS} /></Link>
        )) : <p className="text-sm text-slate-400">Non couvert par un contrat.</p>}
      </section>
      <FilesPanel ownerType="equipment" ownerId={id} testid="equipment-files" />
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4">
        <h2 className="mb-2 text-base font-semibold text-white">Historique des interventions</h2>
        {hist && !hist.interventions.length && <p className="text-sm text-slate-400">Aucune intervention sur cet équipement.</p>}
        <ul className="space-y-2">{hist?.interventions.map((h) => <HistoryItem key={h.id} h={h} />)}</ul>
      </section>
      <EquipmentDialog open={edit} onOpenChange={setEdit} initial={e} onSaved={load} />
    </div>
  );
}
